/* 2026-10-06 — Cart and menu accessibility.
   Uses Prestige's existing focus trap.
   Adds modal background isolation, cart focus retention,
   and announcements across full-cart reloads. */

(() => {
  const storageKey = 'plated-cart-a11y-return';
  const isolated = new Map();
  const dialogs = [];
  let pending = null;

  function activeElement() {
    let element = document.activeElement;

    while (element?.shadowRoot?.activeElement) {
      element = element.shadowRoot.activeElement;
    }

    return element;
  }

  function save(value) {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {}
  }

  function announce(message) {
    const drawer = document.getElementById('cart-drawer');

    const holder =
      (drawer?.open &&
        drawer.shadowRoot?.querySelector('[data-cart-status]')) ||
      document.getElementById('status-announcement');

    if (!holder || !message) return;

    holder.textContent = '';

    setTimeout(() => {
      holder.textContent = message;
    }, 100);
  }

  const isolationObserver = new MutationObserver(() => isolate());

  function isolate() {
    isolated.forEach((wasInert, element) => {
      element.inert = wasInert;
    });

    isolated.clear();

    const modal = [...dialogs]
      .reverse()
      .find((element) => element.isConnected && element.open);

    if (!modal) {
      isolationObserver.disconnect();
      return;
    }

    isolationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Never make an ancestor of the active modal inert.
    let branch = modal;

    while (branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (
          sibling === branch ||
          sibling.matches(
            'script,style,template,[role="status"],[role="alert"]'
          )
        ) {
          continue;
        }

        isolated.set(sibling, sibling.inert);
        sibling.inert = true;
      }

      branch = branch.parentElement;
      if (branch === document.body) break;
    }
  }

  document.addEventListener('dialog:after-show', (event) => {
    const dialog = event.target;

    if (!dialog.matches('[aria-modal="true"]')) return;

    const index = dialogs.indexOf(dialog);
    if (index !== -1) dialogs.splice(index, 1);

    dialogs.push(dialog);
    isolate();
  }, true);

  document.addEventListener('dialog:before-hide', (event) => {
    const index = dialogs.indexOf(event.target);
    if (index !== -1) dialogs.splice(index, 1);

    // Release the background before the existing trap returns focus.
    isolate();
  }, true);

  function describe(control) {
    const scope = control.closest('cart-drawer,.cart-page');
    if (!scope) return null;

    const discount = control.closest(
      '.cart-discount,cart-discount-remove-button'
    );

    if (discount) {
      return { kind: 'discount' };
    }

    const quantity = control.closest('line-item-quantity');
    if (!quantity) return null;

    const input = quantity.querySelector('input[data-line-key]');
    if (!input) return null;

    const buttons = [
      ...quantity.querySelectorAll('.quantity-selector__button')
    ];

    const inputs = [
      ...scope.querySelectorAll('input[data-line-key]')
    ].filter((input) => input.getClientRects().length > 0);

    const item =
      quantity.closest('tr') || quantity.closest('line-item');

    return {
      kind: 'quantity',
      lineKey: input.dataset.lineKey,
      index: inputs.indexOf(input),
      title:
        item?.querySelector('.line-item-info a')
          ?.textContent.trim() || 'Item',
      action:
        control.matches('a') ? 'remove' :
        control === buttons[0] ? 'decrease' :
        control === buttons[1] ? 'increase' :
        'input'
    };
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('button,a,input');
    if (control) pending = describe(control);
  }, true);

  document.addEventListener('change', (event) => {
    if (
      event.target.matches('input[data-line-key]') &&
      (
        event.isTrusted ||
        !pending ||
        pending.lineKey !== event.target.dataset.lineKey
      )
    ) {
      pending = describe(event.target);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Enter' &&
      event.target.closest('.cart-discount')
    ) {
      pending = describe(event.target);
    }
  }, true);

  function restore(scope, descriptor) {
    if (!descriptor) return;

    const visible = (element) =>
      element &&
      !element.disabled &&
      element.getClientRects().length > 0;

    let target;

    if (descriptor.kind === 'discount') {
      target = scope.querySelector(
        '.cart-discount input[type="text"]'
      );
    } else {
      // Use only quantity controls visible at this screen size.
      const inputs = [
        ...scope.querySelectorAll('input[data-line-key]')
      ].filter(visible);

      const input = inputs.find(
        (element) => element.dataset.lineKey === descriptor.lineKey
      );

      if (input) {
        const buttons = [
          ...input.closest('line-item-quantity')
            .querySelectorAll('.quantity-selector__button')
        ];

      target =
        descriptor.action === 'decrease' ? buttons[0] :
        descriptor.action === 'increase' ? buttons[1] :
        descriptor.action === 'remove'
          ? input.closest('line-item-quantity')
              .querySelector('a[href*="/cart/change"]')
          : input;

        // Use the input if the button has become disabled.
        if (!visible(target)) target = input;
      } else if (inputs.length) {
        // Removed item: next item's input, or previous if last.
        const index = Math.max(
          0,
          Math.min(descriptor.index ?? 0, inputs.length - 1)
        );

        target = inputs[index];
      }
    }

    if (!visible(target)) {
      target =
        scope.shadowRoot?.querySelector(
          'button[part~="close-button"]'
        ) ||
        document.querySelector(
          'main .empty-state a[href]'
        ) ||
        document.querySelector('main h1');
    }

    if (!target) return;

    if (target.matches('h1')) {
      target.setAttribute('tabindex', '-1');
    }

    // Bring the restored control into view.
    target.focus({ preventScroll: false });
  }

  document.addEventListener('cart-drawer:refreshed', (event) => {
    const descriptor = pending;
    pending = null;

    if (!descriptor || !event.target.open) return;

    const current = activeElement();

    // Preserve focus if the shopper moved to a surviving control.
    if (
      current &&
      current !== document.body &&
      current.isConnected &&
      event.target.contains(current)
    ) {
      return;
    }

    restore(event.target, descriptor);
  });

  let pageUpdateSequence = 0;

document.addEventListener('cart:a11y-page-update', async (event) => {
  const sequence = ++pageUpdateSequence;

  const descriptor = pending || {
    kind: 'quantity',
    lineKey: event.detail.lineKey,
    index: 0,
    action: 'input',
    title: 'Item'
  };

  const item = event.detail.cart.items.find(
    (item) => item.key === event.detail.lineKey
  );

  const message = item
    ? `${item.product_title || item.title} quantity updated to ${item.quantity}`
    : `${descriptor.title} removed from your cart`;

  const section = document.querySelector('.cart-page')
    ?.closest('.shopify-section');

  try {
    if (!section?.id) {
      throw new Error('Cart section not found');
    }

    const url = new URL(window.location.href);
    url.searchParams.set(
      'section_id',
      section.id.replace(/^shopify-section-/, '')
    );

    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Cart section could not be refreshed');
    }

    const html = await response.text();

    // Ignore an older section refresh if another update has started.
    if (sequence !== pageUpdateSequence) return;

    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const replacement = parsed.getElementById(section.id);

    if (
      !replacement ||
      !replacement.querySelector('.cart-page,.empty-state')
    ) {
      throw new Error('Updated cart markup not found');
    }

    // Capture focus immediately before replacing the cart.
    // The shopper may already have pressed Tab.
    const current = activeElement();
    const focusInside = current && section.contains(current);
    const focusLost = !current || current === document.body;

    const currentDescriptor = focusInside
      ? describe(current)
      : null;

    // Preserve other controls, such as checkout, by their DOM position.
    const path = [];

    if (focusInside && currentDescriptor?.kind !== 'quantity') {
      let node = current;

      while (node && node !== section) {
        path.unshift(
          [...node.parentElement.children].indexOf(node)
        );

        node = node.parentElement;
      }
    }

    section.replaceChildren(
      ...[...replacement.childNodes].map(
        (node) => document.importNode(node, true)
      )
    );

    const scope = section.querySelector('.cart-page') || section;

    if (focusInside) {
      if (currentDescriptor?.kind === 'quantity') {
        restore(scope, currentDescriptor);
      } else {
        let target = section;

        for (const index of path) {
          target = target?.children[index];
        }

        if (
          target?.matches('a[href],button,input,textarea,select') &&
          !target.disabled &&
          target.getClientRects().length
        ) {
          target.focus({ preventScroll: false });
        } else {
          restore(scope, descriptor);
        }
      }
    } else if (focusLost) {
      restore(scope, descriptor);
    }

    // Focus outside the cart remains where the shopper moved it.
    pending = null;
    announce(message);
  } catch (error) {
    if (sequence !== pageUpdateSequence) return;

    // Fall back to the existing reload recovery if rendering fails.
    save({ descriptor, message });
    window.location.reload();
  }
});

  document.addEventListener(
    'cart:a11y-discount-success',
    (event) => {
      if (window.themeVariables.settings.pageType === 'cart') {
        save({
          descriptor: pending || { kind: 'discount' },
          message: event.detail.message
        });
      } else {
        announce(event.detail.message);
      }
    }
  );

  let saved;

  try {
    saved = JSON.parse(sessionStorage.getItem(storageKey));
    sessionStorage.removeItem(storageKey);
  } catch {}

  if (
    saved &&
    document.querySelector('.cart-page,.empty-state')
  ) {
    requestAnimationFrame(() => {
      restore(
        document.querySelector('.cart-page') || document.body,
        saved.descriptor
      );

      announce(saved.message);
    });
  }
})();